import React from 'react';
import { TopBar } from '../../components/layout/TopBar';
import { Header } from '../../components/layout/Header';
import { Navigation } from '../../components/layout/Navigation';
import { MobileStickyBar } from '../../components/layout/MobileStickyBar';
import { Footer } from '../../components/layout/Footer';
import { useScreenInit } from '../../useScreenInit';
import { Wrench, AlertTriangle, Info, CheckCircle2, MapPin, Signpost, Mail, Building, ArrowRight } from 'lucide-react';
export function InstallInstructions() {
  useScreenInit();
  return <div className="min-h-screen flex flex-col bg-brand-background" data-id="element-1300">
      <TopBar data-id="element-1301" />
      <Header data-id="element-1302" />
      <Navigation data-id="element-1303" />

      <main className="flex-grow" data-id="element-1304">
        {/* Hero Section */}
        <section className="bg-white border-b border-brand-border py-16 lg:py-24" data-id="element-1305">
          <div className="max-w-7xl mx-auto px-4 text-center" data-id="element-1306">
            <span className="inline-block py-1 px-3 rounded-full bg-brand-goldSoft text-brand-charcoal text-xs font-bold tracking-wide mb-4 uppercase border border-brand-gold/30" data-id="element-1307">
              Installation Guides
            </span>
            <h1 className="text-4xl lg:text-5xl xl:text-6xl font-serif font-bold text-brand-charcoal mb-6 leading-tight" data-id="element-1308">
              Mailbox & Sign Installation
            </h1>
            <p className="text-lg lg:text-xl text-brand-muted max-w-3xl mx-auto leading-relaxed" data-id="element-1309">
              Detailed direct burial instructions for street signs and
              residential mailboxes, plus CBU installation diagrams. Please read
              all instructions and safety warnings carefully before beginning
              your project.
            </p>
          </div>
        </section>

        {/* Concrete Warning Callout (Global) */}
        <section className="py-8 bg-brand-background" data-id="element-1310">
          <div className="max-w-5xl mx-auto px-4" data-id="element-1311">
            <div className="bg-brand-maroon/5 border border-brand-maroon/20 rounded-xl p-6 flex flex-col md:flex-row gap-6 items-start" data-id="element-1312">
              <div className="w-12 h-12 rounded-full bg-brand-maroon/10 text-brand-maroon flex items-center justify-center shrink-0" data-id="element-1313">
                <AlertTriangle size={24} data-id="element-1314" />
              </div>
              <div data-id="element-1315">
                <h3 className="text-lg font-bold text-brand-maroon mb-2" data-id="element-1316">
                  CRITICAL: Use DRY Concrete Powder
                </h3>
                <p className="text-brand-charcoal/80 mb-3 text-sm leading-relaxed" data-id="element-1317">
                  <strong data-id="element-1318">
                    Do not follow instructions on the concrete bag.
                  </strong>{' '}
                  Completely wet concrete will cause the post to slowly lean as
                  it dries due to the weight of the system. The dry concrete
                  powder will absorb moisture from the soil to cure over 48-72
                  hours.
                </p>
                <p className="text-brand-charcoal/80 text-sm leading-relaxed mb-3" data-id="element-1319">
                  If you have dry soil/climate, add a small amount of water to
                  the dry concrete powder but not enough to saturate. Do not
                  mix. Then, add water to the soil layer to increase moisture
                  for absorption. Keep the soil damp, not saturated.
                </p>
                <p className="text-brand-charcoal/80 text-sm leading-relaxed font-medium" data-id="element-1320">
                  Heavily-concreted installations can be dangerous for drivers
                  and make future removal very difficult. If a concrete ball is
                  too large to remove later, you cannot re-use the hole and must
                  cut the post at ground level.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Street Sign Instructions */}
        <section className="py-16 bg-white border-b border-brand-border" id="street-signs" data-id="element-1321">
          <div className="max-w-5xl mx-auto px-4" data-id="element-1322">
            <div className="flex items-center gap-4 mb-8" data-id="element-1323">
              <div className="w-14 h-14 rounded-full bg-brand-goldWash text-brand-charcoal flex items-center justify-center shadow-sm" data-id="element-1324">
                <Signpost size={28} data-id="element-1325" />
              </div>
              <h2 className="text-3xl font-serif font-bold text-brand-charcoal" data-id="element-1326">
                Street Sign Direct Burial
              </h2>
            </div>

            <p className="text-brand-muted mb-8 italic text-sm" data-id="element-1327">
              *These instructions apply to systems provided by MidAtlantic
              Mailbox, however they can be used to reference other
              manufacturers. Premiere Decorative Street Sign Systems include set
              screws. Hardware from other providers may differ.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-1328">
              {/* Helpful Tips */}
              <div className="bg-brand-background p-6 rounded-xl border border-brand-border" data-id="element-1329">
                <h3 className="font-bold text-brand-charcoal mb-4 flex items-center gap-2" data-id="element-1330">
                  <Info size={18} className="text-brand-maroon" data-id="element-1331" />
                  Helpful Tips & Guidelines
                </h3>
                <ul className="space-y-3 text-sm text-brand-muted" data-id="element-1332">
                  <li className="flex items-start gap-2" data-id="element-1333">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-1.5 shrink-0" data-id="element-1334"></span>
                    <span data-id="element-1335">
                      We typically install <strong data-id="element-1336">24"-30"</strong> in the
                      ground (if grade allows) for stability, rather than the
                      standard 18".
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1337">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-1.5 shrink-0" data-id="element-1338"></span>
                    <span data-id="element-1339">
                      Hole diameter should be approx.{' '}
                      <strong data-id="element-1340">6"-8" wide</strong>.
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1341">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-1.5 shrink-0" data-id="element-1342"></span>
                    <span data-id="element-1343">
                      <strong data-id="element-1344">Federal guidelines require</strong> a minimum of
                      84" from the bottom of the sign to the road surface. Local
                      jurisdictions may require additional heights.
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1345">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-1.5 shrink-0" data-id="element-1346"></span>
                    <span data-id="element-1347">
                      Federal Highway requires 7' back from the road surface for
                      pedestrian safety.
                    </span>
                  </li>
                  <li className="flex items-start gap-2 font-semibold text-brand-maroon" data-id="element-1348">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0" data-id="element-1349" />
                    <span data-id="element-1350">
                      Always call your local Ms. Utility to mark underground
                      lines prior to digging.
                    </span>
                  </li>
                </ul>
              </div>

              {/* Required Materials */}
              <div className="bg-brand-background p-6 rounded-xl border border-brand-border" data-id="element-1351">
                <h3 className="font-bold text-brand-charcoal mb-4 flex items-center gap-2" data-id="element-1352">
                  <Wrench size={18} className="text-brand-maroon" data-id="element-1353" />
                  Required Materials
                </h3>
                <ul className="space-y-2 text-sm text-brand-muted" data-id="element-1354">
                  <li className="flex items-center gap-2" data-id="element-1355">
                    <CheckCircle2 size={16} className="text-brand-green" data-id="element-1356" /> 12'
                    - 14' Pole
                  </li>
                  <li className="flex items-center gap-2" data-id="element-1357">
                    <CheckCircle2 size={16} className="text-brand-green" data-id="element-1358" />{' '}
                    Finial Topper with (2) Set Screws
                  </li>
                  <li className="flex items-center gap-2" data-id="element-1359">
                    <CheckCircle2 size={16} className="text-brand-green" data-id="element-1360" /> Base
                    with (3) or (4) Set Screws
                  </li>
                  <li className="flex items-center gap-2" data-id="element-1361">
                    <CheckCircle2 size={16} className="text-brand-green" data-id="element-1362" /> Sign
                    Brackets & Sign Blades
                  </li>
                  <li className="flex items-center gap-2" data-id="element-1363">
                    <CheckCircle2 size={16} className="text-brand-green" data-id="element-1364" />{' '}
                    Minimum 5/8" Bolt for Bracket Installation
                  </li>
                  <li className="flex items-center gap-2" data-id="element-1365">
                    <CheckCircle2 size={16} className="text-brand-green" data-id="element-1366" />{' '}
                    Digging Iron & Tamper Bar
                  </li>
                  <li className="flex items-center gap-2" data-id="element-1367">
                    <CheckCircle2 size={16} className="text-brand-green" data-id="element-1368" />{' '}
                    Allen Key/Allen Wrench
                  </li>
                  <li className="flex items-center gap-2" data-id="element-1369">
                    <CheckCircle2 size={16} className="text-brand-green" data-id="element-1370" /> 60lb
                    Bag of DRY Concrete Powder (SAKRETE recommended)
                  </li>
                </ul>
              </div>
            </div>

            {/* Steps */}
            <h3 className="font-bold text-brand-charcoal text-xl mb-6" data-id="element-1371">
              Assembly & Installation Steps
            </h3>
            <p className="text-brand-maroon font-medium mb-6 text-sm" data-id="element-1372">
              *Assemble the street sign system PRIOR to installing in the
              ground.
            </p>

            <div className="space-y-4" data-id="element-1373">
              {['Measure 24"-30" starting from the bottom of the pole. Mark this measurement. This will be the section installed into the ground.', 'On the top of the pole, install the finial topper using the (2) set screws provided and an Allen Wrench. Mark the holes for the sign bracket allowing your desired distance between the finial and bracket.', 'Before drilling, measure to ensure a minimum of 84" between the bottom of the sign bracket and the mark made in Step 1 (Federal Guidelines). If installing a dual sign, rotate the pole and mark holes for the second sign. Drill the holes.', 'Attach your sign bracket to the pole using a minimum of 5/8" bolts. For Helicopter Mounted signs, attach BOTTOM bracket first, then TOP. Tighten blades & brackets using set screws.', 'Gently loosen the screws at the top of the base to guide the post through.', 'Slide base up the bottom of the pole (careful not to scratch). Raise it several feet above the 24"-30" line to ensure space for packing concrete later. Temporarily set it with (1) set screw.', 'Dig your hole: 24"-30" deep and about 6"-8" in diameter.', 'Place your assembled sign system into the hole.', 'Once level, fill the hole with DRY concrete powder up to 12" or half-way. Tamp the dry concrete around the post and ensure it is level. (See DRY concrete warning above).', 'Fill in the remaining hole with layers of dirt, tamping and packing as you go. Check frequently that your post is level. Pack dirt tightly.', 'Loosen the temporary set screw from Step 6. Lower the base to the ground. Set the base with all set screws. Level unit once more and pack dirt if needed. Let cure 48-72 hours.'].map((step, idx) => <div key={idx} className="flex gap-4 p-4 bg-white border border-brand-border rounded-lg shadow-sm" data-id="element-1374">
                  <div className="w-8 h-8 rounded-full bg-brand-charcoal text-white flex items-center justify-center font-bold shrink-0" data-id="element-1375">
                    {idx + 1}
                  </div>
                  <p className="text-brand-muted text-sm pt-1 leading-relaxed" data-id="element-1376">
                    {step}
                  </p>
                </div>)}
            </div>
            <p className="mt-6 text-sm text-brand-muted italic" data-id="element-1377">
              If touch-ups are needed, we recommend Rust-Oleum Textured Black
              Spray Paint after the unit has set.
            </p>
          </div>
        </section>

        {/* Residential Mailbox Instructions */}
        <section className="py-16 bg-brand-goldWash border-b border-brand-border" id="residential" data-id="element-1378">
          <div className="max-w-5xl mx-auto px-4" data-id="element-1379">
            <div className="flex items-center gap-4 mb-8" data-id="element-1380">
              <div className="w-14 h-14 rounded-full bg-white text-brand-maroon flex items-center justify-center shadow-sm" data-id="element-1381">
                <Mail size={28} data-id="element-1382" />
              </div>
              <h2 className="text-3xl font-serif font-bold text-brand-charcoal" data-id="element-1383">
                Residential Mailbox Direct Burial
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-1384">
              {/* USPS Guidelines & Tips */}
              <div className="bg-white p-6 rounded-xl border border-brand-border shadow-sm" data-id="element-1385">
                <h3 className="font-bold text-brand-charcoal mb-4 flex items-center gap-2" data-id="element-1386">
                  <MapPin size={18} className="text-brand-maroon" data-id="element-1387" />
                  USPS Guidelines & Tips
                </h3>
                <ul className="space-y-3 text-sm text-brand-muted" data-id="element-1388">
                  <li className="flex items-start gap-2" data-id="element-1389">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-1.5 shrink-0" data-id="element-1390"></span>
                    <span data-id="element-1391">
                      Mailbox door must be <strong data-id="element-1392">6-8 inches back</strong> from
                      the curb (or road if no curb).
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1393">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-1.5 shrink-0" data-id="element-1394"></span>
                    <span data-id="element-1395">
                      Bottom of the mailbox must sit{' '}
                      <strong data-id="element-1396">41-45 inches above</strong> the road surface.
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1397">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-1.5 shrink-0" data-id="element-1398"></span>
                    <span data-id="element-1399">
                      Holes are generally <strong data-id="element-1400">16-18 inches deep</strong> and{' '}
                      <strong data-id="element-1401">8"-10" wide</strong>.
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1402">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-1.5 shrink-0" data-id="element-1403"></span>
                    <span data-id="element-1404">
                      Check with HOA, subdivision rules, and local/state laws
                      prior to installation.
                    </span>
                  </li>
                  <li className="flex items-start gap-2 font-semibold text-brand-maroon" data-id="element-1405">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0" data-id="element-1406" />
                    <span data-id="element-1407">
                      If not installed to USPS guidelines, the postal service
                      may deny deliveries.
                    </span>
                  </li>
                </ul>
              </div>

              {/* Required Materials */}
              <div className="bg-white p-6 rounded-xl border border-brand-border shadow-sm" data-id="element-1408">
                <h3 className="font-bold text-brand-charcoal mb-4 flex items-center gap-2" data-id="element-1409">
                  <Wrench size={18} className="text-brand-maroon" data-id="element-1410" />
                  Required Materials
                </h3>
                <ul className="space-y-2 text-sm text-brand-muted" data-id="element-1411">
                  <li className="flex items-center gap-2" data-id="element-1412">
                    <CheckCircle2 size={16} className="text-brand-green" data-id="element-1413" />{' '}
                    Post-Hole Digger or Shovel
                  </li>
                  <li className="flex items-center gap-2" data-id="element-1414">
                    <CheckCircle2 size={16} className="text-brand-green" data-id="element-1415" />{' '}
                    Screwdriver
                  </li>
                  <li className="flex items-center gap-2" data-id="element-1416">
                    <CheckCircle2 size={16} className="text-brand-green" data-id="element-1417" />{' '}
                    Digging Iron
                  </li>
                  <li className="flex items-center gap-2" data-id="element-1418">
                    <CheckCircle2 size={16} className="text-brand-green" data-id="element-1419" />{' '}
                    Tamper Bar or Leveling Tool
                  </li>
                  <li className="flex items-center gap-2" data-id="element-1420">
                    <CheckCircle2 size={16} className="text-brand-green" data-id="element-1421" />{' '}
                    Allen Key/Allen Wrench
                  </li>
                  <li className="flex items-center gap-2" data-id="element-1422">
                    <CheckCircle2 size={16} className="text-brand-green" data-id="element-1423" /> 60lb
                    Bag of DRY Concrete Powder
                  </li>
                </ul>
                <p className="mt-4 text-xs text-brand-muted italic" data-id="element-1424">
                  *Mailbox assembly may differ by manufacturer. These are for
                  standard in-ground posts.
                </p>
              </div>
            </div>

            {/* Steps */}
            <h3 className="font-bold text-brand-charcoal text-xl mb-6" data-id="element-1425">
              Installation Steps
            </h3>
            <div className="space-y-4" data-id="element-1426">
              <div className="flex gap-4 p-4 bg-white border border-brand-border rounded-lg shadow-sm" data-id="element-1427">
                <div className="w-8 h-8 rounded-full bg-brand-charcoal text-white flex items-center justify-center font-bold shrink-0" data-id="element-1428">
                  1
                </div>
                <div className="text-brand-muted text-sm pt-1 leading-relaxed" data-id="element-1429">
                  <p className="mb-2" data-id="element-1430">
                    Fully assemble mailbox system. Set aside. Double check
                    measurements for hole placement (door 6-8 inches from curb).
                  </p>
                  <p data-id="element-1431">
                    Dig hole approx. 16-18 inches deep and 8-10 inches wide.
                    Place mailbox and post into hole and hold steady (a second
                    person is helpful).
                  </p>
                </div>
              </div>
              <div className="flex gap-4 p-4 bg-white border border-brand-border rounded-lg shadow-sm" data-id="element-1432">
                <div className="w-8 h-8 rounded-full bg-brand-charcoal text-white flex items-center justify-center font-bold shrink-0" data-id="element-1433">
                  2
                </div>
                <div className="text-brand-muted text-sm pt-1 leading-relaxed" data-id="element-1434">
                  <p className="mb-2" data-id="element-1435">
                    Level the system. Pour about 1/4 bag of DRY concrete powder
                    into the hole. Tamp around post and ensure level.
                  </p>
                  <p data-id="element-1436">
                    Add soil on top of concrete and tamp down. Repeat process
                    with another 1/4 bag of dry concrete. Fill remaining 3
                    inches with dirt until secure.
                  </p>
                </div>
              </div>
              <div className="flex gap-4 p-4 bg-white border border-brand-border rounded-lg shadow-sm" data-id="element-1437">
                <div className="w-8 h-8 rounded-full bg-brand-charcoal text-white flex items-center justify-center font-bold shrink-0" data-id="element-1438">
                  3
                </div>
                <div className="text-brand-muted text-sm pt-1 leading-relaxed" data-id="element-1439">
                  <p className="mb-2" data-id="element-1440">
                    Tamp the top layer of dirt flat.{' '}
                    <strong data-id="element-1441">MAILBOX WILL BE FRAGILE.</strong> Do not shake,
                    move, or use strong force for 48 hours. Fully cured in 72
                    hours.
                  </p>
                  <p className="text-brand-maroon font-medium" data-id="element-1442">
                    Moving/shaking while drying creates a concrete-casted hole
                    with gaping, causing a wobbly mailbox.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CBU Installation */}
        <section className="py-16 bg-white border-b border-brand-border" id="cbu" data-id="element-1443">
          <div className="max-w-5xl mx-auto px-4" data-id="element-1444">
            <div className="flex items-center gap-4 mb-8" data-id="element-1445">
              <div className="w-14 h-14 rounded-full bg-brand-charcoal text-brand-gold flex items-center justify-center shadow-sm" data-id="element-1446">
                <Building size={28} data-id="element-1447" />
              </div>
              <h2 className="text-3xl font-serif font-bold text-brand-charcoal" data-id="element-1448">
                Cluster Box (CBU) 1570 F Series
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12" data-id="element-1449">
              <div className="bg-brand-background p-6 rounded-xl border border-brand-border" data-id="element-1450">
                <h3 className="font-bold text-brand-charcoal mb-4" data-id="element-1451">
                  CBU Installation Heights
                </h3>
                <ul className="space-y-2 text-sm text-brand-muted" data-id="element-1452">
                  <li data-id="element-1453">
                    <strong data-id="element-1454">Type I:</strong> 8 Tenant Doors
                  </li>
                  <li data-id="element-1455">
                    <strong data-id="element-1456">Type II:</strong> 12 Tenant Doors
                  </li>
                  <li data-id="element-1457">
                    <strong data-id="element-1458">Type III:</strong> 16 Tenant Doors
                  </li>
                  <li data-id="element-1459">
                    <strong data-id="element-1460">Type IV:</strong> 13 Tenant Doors
                  </li>
                </ul>
              </div>

              <div className="lg:col-span-2 bg-brand-background p-6 rounded-xl border border-brand-border" data-id="element-1461">
                <h3 className="font-bold text-brand-charcoal mb-4" data-id="element-1462">
                  Pedestal Installation Steps
                </h3>
                <ol className="list-decimal list-inside space-y-2 text-sm text-brand-muted" data-id="element-1463">
                  <li data-id="element-1464">Open Master Loading Doors</li>
                  <li data-id="element-1465">
                    Remove Left and Right Side 91079 Floating Shelves, exposing
                    mounting holes.
                  </li>
                  <li data-id="element-1466">Place unit on Pedestal</li>
                  <li data-id="element-1467">
                    From underneath unit, insert Four (4) 86101 3/8-16x1.25"
                    Carriage Bolts upward.
                  </li>
                  <li data-id="element-1468">
                    From inside parcel compartments, add four (4) 86097 3/8" x
                    .688 OD Lock Washer and tighten four (4) 86089B 3/8"-16 hex
                    nuts. Torque to 22 foot-pounds.
                  </li>
                  <li data-id="element-1469">Replace Left and Right Side 91079 Floating Shelves.</li>
                </ol>
              </div>
            </div>

            {/* Diagrams */}
            <h3 className="font-bold text-brand-charcoal text-xl mb-6" data-id="element-1470">
              Installation Diagrams
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1471">
              <div className="border border-brand-border rounded-xl overflow-hidden shadow-sm bg-white p-4" data-id="element-1472">
                <p className="text-center font-bold text-brand-charcoal text-sm mb-3" data-id="element-1473">
                  CBU Heights
                </p>
                <img src="https://drwzpk38qkpfb.cloudfront.net/www.midatlanticmailboxes.com/uploaded/images/authflorence/authflorence/cbuheight.jpg" alt="CBU Installation Heights Diagram" className="w-full h-auto object-contain" data-id="element-1474" />
              </div>
              <div className="border border-brand-border rounded-xl overflow-hidden shadow-sm bg-white p-4" data-id="element-1475">
                <p className="text-center font-bold text-brand-charcoal text-sm mb-3" data-id="element-1476">
                  Site & Pad Prep
                </p>
                <img src="https://drwzpk38qkpfb.cloudfront.net/www.midatlanticmailboxes.com/uploaded/images/authflorence/authflorence/paddetail.jpg" alt="CBU Site and Pad Preparation Diagram" className="w-full h-auto object-contain" data-id="element-1477" />
              </div>
              <div className="border border-brand-border rounded-xl overflow-hidden shadow-sm bg-white p-4" data-id="element-1478">
                <p className="text-center font-bold text-brand-charcoal text-sm mb-3" data-id="element-1479">
                  Pedestal Install
                </p>
                <img src="https://drwzpk38qkpfb.cloudfront.net/www.midatlanticmailboxes.com/uploaded/images/authflorence/authflorence/pedestalinstall.jpg" alt="CBU Pedestal Installation Diagram" className="w-full h-auto object-contain" data-id="element-1480" />
              </div>
            </div>
          </div>
        </section>

        {/* Footer Contact Banner */}
        <section className="py-12 bg-brand-charcoal text-center" data-id="element-1481">
          <div className="max-w-3xl mx-auto px-4" data-id="element-1482">
            <p className="text-brand-gold font-bold mb-2" data-id="element-1483">
              MidAtlantic Mailbox, Inc
            </p>
            <p className="text-gray-300 text-sm mb-4" data-id="element-1484">
              4507 Metropolitan Court, Suite D | Frederick, MD 21704
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-white text-sm" data-id="element-1485">
              <span data-id="element-1486">
                Toll Free:{' '}
                <a href="tel:800-806-7997" className="hover:text-brand-gold" data-id="element-1487">
                  800-806-7997
                </a>
              </span>
              <span className="hidden sm:inline text-gray-500" data-id="element-1488">|</span>
              <span data-id="element-1489">
                Local:{' '}
                <a href="tel:301-620-7585" className="hover:text-brand-gold" data-id="element-1490">
                  301-620-7585
                </a>{' '}
                or{' '}
                <a href="tel:301-620-7867" className="hover:text-brand-gold" data-id="element-1491">
                  301-620-7867
                </a>
              </span>
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-1492" />
      <MobileStickyBar data-id="element-1493" />
    </div>;
}